/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { Controller, useForm } from 'react-hook-form'
import { doc, collection, DocumentSnapshot, updateDoc, onSnapshot, getDoc, addDoc } from 'firebase/firestore'
import { db } from '../../../firebase'
import { Box, Button, Typography, Backdrop, CircularProgress, Modal, Stack, FormControl, InputLabel, Select, MenuItem, FormHelperText, Container, TextField } from "@mui/material"
import dayjs from "dayjs"
import { toast } from 'react-toastify'
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import isEmpty from "lodash.isempty"
import useCollectionSubscription from "../../../components/hooks/useCollectionSubscription"
import { Customer } from "../../../models/customer"

export type ShipmentProductSnapshot = {
  id: string
  productNo: string
  individualNo?: string
  part?: string
  weight?: number | string
  storageType?: string
  quantity: number
  price: number
  taxRate?: number
}

type Props = {
  productId?: string
  productIds?: string[]
  productSnapshots?: ShipmentProductSnapshot[]
  isOpen: boolean
  onClose: () => void
  onShippingComplete?: () => void
}

const ShippingDestinationForm = (props: Props) => {
  const { productId, productIds, productSnapshots, isOpen, onClose, onShippingComplete } = props
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([])
  const [productInfoList, setProductInfoList] = useState<any[]>([])
  const { control, handleSubmit, reset } = useForm({})
  const [open, setOpen] = useState<boolean>(false)
  const [prosessing, setProcessing] = useState<boolean>(false)
  const [formValues, setFormValues] = useState<Record<string, any>|null>()
  const productsRef = collection(db, 'products')
  const shipmentsRef = collection(db, 'shipments')
  const customers = useCollectionSubscription(collection(db, 'customers'), Customer)

  useEffect(() => {
    if (!isEmpty(formValues) && formValues.customer) {
      reset({
        shippingDate: dayjs(formValues.shippingDate),
        customer: formValues.customer,
        shippingFee: formValues.shippingFee ?? 0,
        shippingMemo: formValues.shippingMemo
      })
    }
    else {
      reset({
        shippingDate: dayjs(),
        customer: "",
        shippingFee: 0,
        shippingMemo: ""
      })
    }
  }, [formValues])

  // 単一商品の場合
  useEffect(() => {
    if (productId) {
      setSelectedProductIds([productId])
    }
  }, [productId])

  // 複数商品の場合
  useEffect(() => {
    if (productIds && productIds.length > 0) {
      setSelectedProductIds(productIds)
    }
  }, [productIds])

  // 選択された商品情報を取得
  useEffect(() => {
    if (!isOpen || selectedProductIds.length === 0) return

    setOpen(isOpen)
    setProductInfoList([])

    // 単一商品の場合は従来の動作を維持
    if (selectedProductIds.length === 1 && productId) {
      onSnapshot(doc(productsRef, productId), (_: DocumentSnapshot) => {
        if (_.exists()) {
          const data = _.data()
          setFormValues(data)
        }
      })
    } else {
      // 複数商品の場合は最初の商品の情報をフォームの初期値として使用
      const firstProductId = selectedProductIds[0]
      onSnapshot(doc(productsRef, firstProductId), (_: DocumentSnapshot) => {
        if (_.exists()) {
          const data = _.data()
          setFormValues(data)
        }
      })

      // すべての選択された商品の情報を取得
      const fetchProductInfo = async () => {
        const productInfos: any[] = []
        for (const id of selectedProductIds) {
          const docSnap = await getDoc(doc(productsRef, id))
          if (docSnap.exists()) {
            productInfos.push({
              id,
              ...docSnap.data()
            })
          }
        }
        setProductInfoList(productInfos)
      }

      fetchProductInfo()
    }
  }, [selectedProductIds, isOpen])

  const submit = async ({shippingDate, customer, shippingFee, shippingMemo}: Record<string, any>) => {
    setProcessing(true)
    try {
      const shippingDateStr = shippingDate.format("YYYY/MM/DD")
      const now = dayjs().format('YYYY/MM/DD HH:mm')
      const shippingFeeValue = Math.max(0, parseInt(shippingFee) || 0)

      // 出荷スナップショットを shipments コレクションに保存
      const snapshotMap = new Map<string, ShipmentProductSnapshot>()
      if (productSnapshots) {
        productSnapshots.forEach((s) => snapshotMap.set(s.id, s))
      }

      const productsForShipment = selectedProductIds.map((id) => {
        const snap = snapshotMap.get(id)
        if (snap) {
          return {
            id,
            productNo: snap.productNo,
            individualNo: snap.individualNo ?? null,
            part: snap.part ?? null,
            weight: snap.weight ?? null,
            storageType: snap.storageType ?? null,
            quantity: snap.quantity,
            price: snap.price,
            taxRate: snap.taxRate === 10 ? 10 : 8,
          }
        }
        // スナップショット未提供の場合は productInfoList / formValues から最小限を埋める
        // 部位マスタの税率が分からないため、未設定の部位と同様に8%（軽減税率）とみなす
        const info = productInfoList.find((p) => p.id === id) || (id === productId ? formValues : null)
        return {
          id,
          productNo: info?.productNo ?? null,
          individualNo: info?.individualNo ?? null,
          part: info?.part ?? null,
          weight: info?.weight ?? null,
          storageType: info?.storageType ?? null,
          quantity: 1,
          price: 0,
          taxRate: 8,
        }
      })

      const totalCount = productsForShipment.reduce((sum, p) => sum + (p.quantity || 1), 0)
      const totalWeight = productsForShipment.reduce((sum, p) => {
        const w = typeof p.weight === 'string'
          ? parseFloat(p.weight.replace(/[^\d.-]/g, '')) || 0
          : (typeof p.weight === 'number' ? p.weight : 0)
        return sum + w * (p.quantity || 1)
      }, 0)
      const totalPrice = productsForShipment.reduce((sum, p) => sum + (p.price || 0), 0)

      const shipmentRef = await addDoc(shipmentsRef, {
        shippingDate: shippingDateStr,
        customer,
        trackingNumber: "",
        shippingFee: shippingFeeValue,
        shippingMemo: shippingMemo || "",
        products: productsForShipment,
        totalCount,
        totalWeight,
        totalPrice,
        createdAt: now,
      })

      // 商品側にも出荷情報詳細へのリンク用に出荷IDを残す
      const updateData = {
        shippingDate: shippingDateStr,
        customer,
        shippingMemo: shippingMemo || "",
        isSold: true,
        shipmentId: shipmentRef.id,
        updatedAt: now
      }

      await Promise.all(
        selectedProductIds.map(id => updateDoc(doc(productsRef, id), updateData))
      )

      toast.success(`${selectedProductIds.length}件の商品の出荷先情報を登録しました`)

      // 出荷登録成功時のコールバックを実行
      if (onShippingComplete) {
        onShippingComplete()
      }
    }
    catch (e) {
      toast.error(JSON.stringify(e))
    }
    finally {
      reset()
      setProcessing(false)
      setOpen(false)
      onClose()
    }
  }

  const close = () => {
    setOpen(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClose={onClose}
      sx={{ padding: 2 }}
    >
      <Container maxWidth="xs" sx={{ mt: 6 }}>
        <Box
          component="form"
          onSubmit={handleSubmit(submit)}
          display="flex-col"
          justifyContent="center"
          alignItems="center"
          bgcolor="white"
        >
          <Box
            mx={2}
            width="50vh"
            display="flex-col"
          >
            <Box display="flex" justifyContent="start" m={2}>
              {selectedProductIds.length === 1 ? (
                <Typography variant="h5" mt={2}>{`製品番号：${formValues && formValues.productNo}`}</Typography>
              ) : (
                <Typography variant="h5" mt={2}>{`選択された商品数：${selectedProductIds.length}件`}</Typography>
              )}
            </Box>
            {selectedProductIds.length > 1 && productInfoList.length > 0 && (
              <Box m={2}>
                <Typography variant="subtitle1">選択された商品番号：</Typography>
                <Box sx={{ maxHeight: '100px', overflowY: 'auto', mt: 1 }}>
                  {productInfoList.map((product, index) => (
                    <Typography key={index} variant="body2">{product.productNo}</Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <Stack spacing={4} border={1} borderRadius={1} borderColor="#333" p={3} mx={2}>
            <Box display="flex" justifyContent="start">
              <Controller
                name="shippingDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) {
                      return '出荷日の入力は必須です'
                    }
                    if (!dayjs(value).isValid()) {
                      return '日付形式が間違っています'
                    }
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="ja"
                  >
                    <DatePicker
                      {...field}
                      label="出荷日"
                      value={field.value}
                      format="YYYY/MM/DD"
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message
                        }
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Controller
                name="customer"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel id="select-label">取引先</InputLabel>
                    <Select
                      labelId="select-label"
                      id="select"
                      label="取引先"
                      {...field}
                    >
                      <MenuItem value="自社">自社</MenuItem>
                      {
                        customers.map((c:Customer) => {
                          return <MenuItem value={c.companyName}>{c.companyName}</MenuItem>
                        })
                      }
                    </Select>
                    <FormHelperText>{error?.message || ""}</FormHelperText>
                  </FormControl>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center" my={1}>
              <Controller
                name="shippingFee"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="送料（税込）"
                    fullWidth
                    placeholder="送料を入力"
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{ min: 0, step: 100 }}
                  />
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center" my={1}>
              <Controller
                name="shippingMemo"
                control={control}
                defaultValue={formValues && formValues.memo}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    label="備考"
                    fullWidth
                    placeholder="備考"
                  />
                )}
              />
            </Box>
          </Stack>
          <Box m={1} p={1} display="flex" justifyContent="center">
            <Button variant="outlined" color="warning" onClick={close}>キャンセル</Button>
            <Button variant="contained" color="info" onClick={handleSubmit(submit)} sx={{ ml: 2 }}>登録</Button>
          </Box>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={prosessing}
            onClick={() => setProcessing(false)}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      </Container>
    </Modal>

  )
}
export default ShippingDestinationForm
